import { z } from "zod";
import { staffRoleSchema } from "./enums";

/**
 * Invite codes: 8 symbols from Crockford's base32 alphabet, which leaves out
 * I, L, O and U so a code read aloud or typed is hard to get wrong. Input is
 * forgiving: case, spaces and hyphens are ignored, and O, I and L are read as
 * 0, 1 and 1. See docs/architecture/auth-tenancy.md.
 */

export const INVITE_CODE_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
export const INVITE_CODE_LENGTH = 8;

export const INVITE_DEFAULT_DAYS = 7;
export const INVITE_MAX_DAYS = 30;
export const INVITE_MAX_USES = 50;

/**
 * The stored form of a typed code, or null if it cannot be one. A pasted
 * join link (`https://app.<root>/join/ABCD2345`) gives its code.
 */
export function normaliseInviteCode(input: string): string | null {
	const link = /\/join\/([^/?#\s]+)/.exec(input);
	const code = (link?.[1] ?? input)
		.toUpperCase()
		.replace(/[\s-]/g, "")
		.replace(/O/g, "0")
		.replace(/[IL]/g, "1");
	if (code.length !== INVITE_CODE_LENGTH) return null;
	for (const ch of code) if (!INVITE_CODE_ALPHABET.includes(ch)) return null;
	return code;
}

/** `ABCD2345` → `ABCD-2345`, for display. */
export function formatInviteCode(code: string): string {
	return `${code.slice(0, 4)}-${code.slice(4)}`;
}

/** Typed input: anything up to 20 characters, normalised by the server. */
export const inviteCodeInputSchema = z.string().trim().min(1).max(20);

export const createInviteInputSchema = z.object({
	/** Client-made UUID, so a retried call returns the same invite. */
	id: z.uuid().optional(),
	role: staffRoleSchema,
	/** Binds the invite to this verified email. */
	email: z
		.email()
		.max(254)
		.transform((e) => e.toLowerCase())
		.nullable()
		.optional(),
	expiresInDays: z.number().int().min(1).max(INVITE_MAX_DAYS).default(INVITE_DEFAULT_DAYS),
	maxUses: z.number().int().min(1).max(INVITE_MAX_USES).default(1),
});
export type CreateInviteInput = z.input<typeof createInviteInputSchema>;

export const inviteSchema = z.object({
	id: z.uuid(),
	code: z.string(),
	/** `https://app.<root>/join/<code>` */
	link: z.url(),
	role: staffRoleSchema,
	email: z.string().nullable(),
	expiresAt: z.iso.datetime(),
	maxUses: z.number().int(),
	usedCount: z.number().int(),
	revokedAt: z.iso.datetime().nullable(),
	createdAt: z.iso.datetime(),
});
export type Invite = z.infer<typeof inviteSchema>;

/** What a person sees before accepting. */
export const invitePreviewSchema = z.object({
	restaurantName: z.string(),
	role: staffRoleSchema,
	/** The caller is an active member already: the app offers to open the restaurant. */
	alreadyMember: z.boolean(),
});
export type InvitePreview = z.infer<typeof invitePreviewSchema>;

/** Why an invite cannot be used. Sent as `details.reason`. */
export const INVITE_PROBLEMS = [
	"invalid",
	"expired",
	"revoked",
	"used_up",
	"email_mismatch",
] as const;
export type InviteProblem = (typeof INVITE_PROBLEMS)[number];
